import { useEffect } from "react";
import { Header } from "@/components/layout/Header";
import { Link } from "react-router-dom";
import footerFunding from "@/assets/footer-funding.jpeg";
import { SEOPresets } from "@/components/SEO";

export default function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary via-accent to-secondary">
      {SEOPresets.termsOfService}
      <Header showLogout solidBackground={true} />

      {/* Main Content */}
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 md:p-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-2 text-center">
              Terms and Conditions
            </h1>
            <p className="text-center text-gray-600 mb-8">
              Sundae Education Technologies SL - GlobalCoders App
            </p>

            <div className="prose prose-slate max-w-none">
              {/* Section 1 */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  1. INTRODUCTION AND ACCEPTANCE
                </h2>
                <p className="text-gray-700 mb-4">
                  These Terms and Conditions ("Terms") govern your use of the
                  GlobalCoders mobile application and related services
                  ("Service") operated by Sundae Education Technologies SL, a
                  company incorporated under Spanish law with registered address
                  at Carrer de Joan Oliver, 22D, 2-1, Sant Cugat del Valles,
                  08172, Spain ("Company", "we", "us", or "our").
                </p>
                <p className="text-gray-700 mb-4">
                  By downloading, installing, accessing, or using our Service,
                  you agree to be bound by these Terms. If you do not agree to
                  these Terms, do not use our Service.
                </p>
                <p className="text-gray-700">
                  These Terms constitute a legally binding agreement between you
                  ("User", "you", or "your") and the Company.
                </p>
              </section>

              {/* Section 2 */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  2. SERVICE DESCRIPTION
                </h2>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  2.1 Overview
                </h3>
                <p className="text-gray-700 mb-4">
                  GlobalCoders is a freemium mobile and web application designed
                  to help users practice and improve their business English
                  communication skills through AI-powered conversation
                  simulations and feedback.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  2.2 Freemium Model
                </h3>
                <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                  <li>
                    Free Tier: Users receive some complimentary simulations as
                    indicated on the website or mobile application
                  </li>
                  <li>
                    Subscription Required: Access to additional simulations
                    requires an active paid subscription
                  </li>
                  <li>
                    Features: The Service includes business conversation
                    practice sessions, AI-generated feedback, performance
                    analytics, and learning resources
                  </li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  2.3 Service Availability
                </h3>
                <p className="text-gray-700">
                  We strive to maintain continuous service availability but do
                  not guarantee uninterrupted access. The Service may be
                  temporarily unavailable due to maintenance, updates, or
                  technical issues.
                </p>
              </section>

              {/* Section 3 */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  3. USER ACCOUNTS
                </h2>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  3.1 Account Creation
                </h3>
                <p className="text-gray-700 mb-4">
                  To use certain features of our Service, you must create an
                  account by providing accurate and complete information,
                  including a valid email address.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  3.2 Account Security
                </h3>
                <p className="text-gray-700 mb-2">You are responsible for:</p>
                <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                  <li>
                    Maintaining the confidentiality of your account credentials
                  </li>
                  <li>All activities that occur under your account</li>
                  <li>
                    Immediately notifying us of any unauthorized use of your
                    account
                  </li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  3.3 Account Eligibility
                </h3>
                <p className="text-gray-700 mb-4">
                  You must be at least 16 years old to create an account. Users
                  under 18 require parental consent where required by applicable
                  law.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  3.4 Account Accuracy
                </h3>
                <p className="text-gray-700">
                  You agree to provide accurate, current, and complete
                  information and to update such information as necessary to
                  maintain its accuracy.
                </p>
              </section>

              {/* Section 4 */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  4. SUBSCRIPTION PLANS AND BILLING
                </h2>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  4.1 Subscription Options
                </h3>
                <p className="text-gray-700 mb-2">
                  We offer the following subscription plans:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                  <li>Monthly Subscription: Billed monthly</li>
                  <li>Quarterly Subscription: Billed every three months</li>
                  <li>Annual Subscription: Billed annually</li>
                </ul>
                <p className="text-gray-700 mb-4">
                  Current pricing is displayed in the application and on our
                  website globalcoders.app.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  4.2 Billing and Payment
                </h3>
                <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                  <li>
                    Subscriptions are billed in advance on a recurring basis
                  </li>
                  <li>
                    Payment is processed through third-party payment processors
                  </li>
                  <li>
                    You authorize us to charge your selected payment method
                  </li>
                  <li>
                    All fees are non-refundable except as expressly stated in
                    these Terms
                  </li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  4.3 Automatic Renewal
                </h3>
                <p className="text-gray-700 mb-4">
                  Subscriptions automatically renew at the end of each billing
                  period unless cancelled before the renewal date. You will be
                  charged the then-current subscription fee.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  4.4 Price Changes
                </h3>
                <p className="text-gray-700 mb-4">
                  We may modify subscription prices with thirty (30) days'
                  advance notice. Price changes will not affect your current
                  billing period but will apply to subsequent renewals.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  4.5 Cancellation
                </h3>
                <p className="text-gray-700 mb-4">
                  You may cancel your subscription at any time through your
                  account settings or by contacting support@globalcoders.app.
                  Cancellation takes effect at the end of your current billing
                  period.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  4.6 Refund Policy
                </h3>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Free Trial: No refunds for free simulations</li>
                  <li>
                    Subscriptions: Generally non-refundable, except for
                    technical issues preventing service use or as required by
                    applicable law
                  </li>
                  <li>
                    EU Consumer Rights: EU consumers may be entitled to refunds
                    under applicable consumer protection laws
                  </li>
                </ul>
              </section>

              {/* Section 5 */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  5. ACCEPTABLE USE POLICY
                </h2>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  5.1 Permitted Use
                </h3>
                <p className="text-gray-700 mb-4">
                  You may use the Service only for lawful purposes and in
                  accordance with these Terms.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  5.2 Prohibited Activities
                </h3>
                <p className="text-gray-700 mb-2">You agree not to:</p>
                <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                  <li>
                    Use the Service for any illegal or unauthorized purpose
                  </li>
                  <li>Violate any applicable laws or regulations</li>
                  <li>Infringe upon intellectual property rights</li>
                  <li>
                    Upload malicious code or attempt to compromise system
                    security
                  </li>
                  <li>
                    Reverse engineer, decompile, or disassemble the Service
                  </li>
                  <li>Share account credentials with third parties</li>
                  <li>
                    Use automated systems to access the Service without
                    authorization
                  </li>
                  <li>Interfere with or disrupt the Service or servers</li>
                  <li>Impersonate others or provide false information</li>
                  <li>Use the Service to harass, abuse, or harm others</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  5.3 Enforcement
                </h3>
                <p className="text-gray-700">
                  We reserve the right to investigate violations and take
                  appropriate action, including account suspension or
                  termination.
                </p>
              </section>

              {/* Section 6 */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  6. INTELLECTUAL PROPERTY RIGHTS
                </h2>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  6.1 Company IP
                </h3>
                <p className="text-gray-700 mb-4">
                  The Service, including all content, features, functionality,
                  software, text, graphics, logos, and trademarks, is owned by
                  the Company and protected by intellectual property laws.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  6.2 Limited License
                </h3>
                <p className="text-gray-700 mb-4">
                  We grant you a limited, non-exclusive, non-transferable,
                  revocable license to use the Service for personal,
                  non-commercial purposes in accordance with these Terms.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  6.3 Restrictions
                </h3>
                <p className="text-gray-700 mb-2">You may not:</p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Copy, modify, distribute, or create derivative works</li>
                  <li>Remove or alter proprietary notices</li>
                  <li>Use our trademarks without written permission</li>
                </ul>
              </section>

              {/* Section 7 */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  7. USER-GENERATED CONTENT
                </h2>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  7.1 Your Content
                </h3>
                <p className="text-gray-700 mb-4">
                  You retain ownership of content you submit to the Service
                  ("User Content"), including simulation responses and feedback.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  7.2 License to Company
                </h3>
                <p className="text-gray-700 mb-4">
                  By submitting User Content, you grant us a worldwide,
                  non-exclusive license to use, reproduce, and display your
                  content for service operation purposes.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  7.3 Content Standards
                </h3>
                <p className="text-gray-700 mb-2">User Content must not:</p>
                <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                  <li>Violate applicable laws or third-party rights</li>
                  <li>
                    Contain offensive, defamatory, or inappropriate material
                  </li>
                  <li>
                    Include personal information of others without consent
                  </li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  7.4 Content Removal
                </h3>
                <p className="text-gray-700">
                  We reserve the right to remove User Content that violates
                  these Terms or applicable law.
                </p>
              </section>

              {/* Section 8 */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  8. SERVICE MODIFICATIONS AND AVAILABILITY
                </h2>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  8.1 Service Changes
                </h3>
                <p className="text-gray-700 mb-4">
                  We may modify, suspend, or discontinue any aspect of the
                  Service at any time with reasonable notice.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  8.2 Updates
                </h3>
                <p className="text-gray-700 mb-4">
                  We may release updates to improve functionality, security, or
                  compliance. Some updates may be mandatory.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  8.3 No Guarantee of Availability
                </h3>
                <p className="text-gray-700">
                  While we strive for high availability, we do not guarantee
                  uninterrupted service and are not liable for temporary
                  unavailability.
                </p>
              </section>

              {/* Section 9 */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  9. TERMINATION AND SUSPENSION
                </h2>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  9.1 Termination by You
                </h3>
                <p className="text-gray-700 mb-4">
                  You may terminate your account at any time by contacting
                  support@globalcoders.app or through account settings.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  9.2 Termination by Company
                </h3>
                <p className="text-gray-700 mb-2">
                  We may suspend or terminate your account immediately if you:
                </p>
                <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
                  <li>Breach these Terms</li>
                  <li>Engage in prohibited activities</li>
                  <li>Fail to pay subscription fees</li>
                  <li>Violate applicable laws</li>
                </ul>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  9.3 Effect of Termination
                </h3>
                <p className="text-gray-700 mb-2">Upon termination:</p>
                <ul className="list-disc pl-6 text-gray-700 space-y-2">
                  <li>Your access to the Service will cease</li>
                  <li>We may delete your account and data</li>
                  <li>Accrued payment obligations remain due</li>
                  <li>
                    Provisions that should survive termination will continue to
                    apply
                  </li>
                </ul>
              </section>

              {/* Section 10 */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  10. DISCLAIMERS AND LIMITATION OF LIABILITY
                </h2>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  10.1 Service Disclaimer
                </h3>
                <p className="text-gray-700 mb-4">
                  THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT
                  WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT
                  LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
                  PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  10.2 No Professional Advice
                </h3>
                <p className="text-gray-700 mb-4">
                  The Service provides educational content and practice tools
                  but does not constitute professional career advice or
                  guarantee success in a business conversation.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  10.3 Limitation of Liability
                </h3>
                <p className="text-gray-700 mb-4">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE COMPANY SHALL NOT
                  BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
                  CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED
                  TO LOSS OF PROFITS, DATA, OR USE, ARISING OUT OF OR RELATING
                  TO THESE TERMS OR THE SERVICE.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  10.4 Liability Cap
                </h3>
                <p className="text-gray-700 mb-4">
                  Our total liability to you for all claims arising out of or
                  relating to these Terms or the Service shall not exceed the
                  amount you paid to us in the twelve (12) months preceding the
                  claim.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  10.5 Consumer Rights
                </h3>
                <p className="text-gray-700">
                  Nothing in these Terms limits your rights as a consumer under
                  applicable law, including EU consumer protection regulations.
                </p>
              </section>

              {/* Section 11 */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  11. INDEMNIFICATION
                </h2>
                <p className="text-gray-700">
                  You agree to indemnify, defend, and hold harmless the Company
                  and its officers, directors, employees, and agents from any
                  claims, damages, losses, or expenses arising out of your use
                  of the Service, violation of these Terms, or infringement of
                  third-party rights.
                </p>
              </section>

              {/* Section 12 */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  12. DISPUTE RESOLUTION AND GOVERNING LAW
                </h2>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  12.1 Governing Law
                </h3>
                <p className="text-gray-700 mb-4">
                  These Terms are governed by Spanish law and EU regulations,
                  without regard to conflict of law principles.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  12.2 Jurisdiction
                </h3>
                <p className="text-gray-700 mb-4">
                  Any disputes arising under these Terms shall be subject to the
                  exclusive jurisdiction of the courts of Barcelona, Spain.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  12.3 EU Consumer Rights
                </h3>
                <p className="text-gray-700 mb-4">
                  EU consumers may also bring proceedings in their country of
                  residence and may benefit from mandatory consumer protection
                  laws.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  12.4 Alternative Dispute Resolution
                </h3>
                <p className="text-gray-700">
                  Before initiating formal proceedings, we encourage users to
                  contact us at support@globalcoders.app to resolve disputes
                  amicably.
                </p>
              </section>

              {/* Section 13 */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  13. GENERAL PROVISIONS
                </h2>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  13.1 Entire Agreement
                </h3>
                <p className="text-gray-700 mb-4">
                  These Terms, together with our Privacy Policy and Cookie
                  Policy, constitute the entire agreement between you and the
                  Company.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  13.2 Amendments
                </h3>
                <p className="text-gray-700 mb-4">
                  We may update these Terms from time to time. Material changes
                  will be communicated with reasonable advance notice. Continued
                  use of the Service after changes constitutes acceptance.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  13.3 Severability
                </h3>
                <p className="text-gray-700 mb-4">
                  If any provision of these Terms is found unenforceable, the
                  remaining provisions will remain in full force and effect.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  13.4 Assignment
                </h3>
                <p className="text-gray-700 mb-4">
                  You may not assign your rights under these Terms. We may
                  assign our rights and obligations without restriction.
                </p>

                <h3 className="text-xl font-semibold text-gray-800 mb-3">
                  13.5 Force Majeure
                </h3>
                <p className="text-gray-700">
                  We are not liable for delays or failures in performance due to
                  circumstances beyond our reasonable control.
                </p>
              </section>

              {/* Section 14 */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">
                  14. CONTACT INFORMATION
                </h2>
                <p className="text-gray-700 mb-4">
                  For questions about these Terms, please contact us:
                </p>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-gray-900 font-semibold">
                    Sundae Education Technologies SL
                  </p>
                  <p className="text-gray-700">
                    Email: support@globalcoders.app
                  </p>
                  <p className="text-gray-700">
                    Address: Carrer de Joan Oliver, 22D, 2-1, Sant Cugat del
                    Valles, 08172, Spain
                  </p>
                  <p className="text-gray-700">Website: globalcoders.app</p>
                </div>
              </section>

              <p className="text-sm text-gray-500 text-center mt-8">
                Last Updated: 21st October 2025
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Professional Footer */}
      <footer className="relative bg-gradient-to-br from-primary via-accent to-primary mt-20">
        {/* Decorative grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:64px_64px]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Main footer content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* About section */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-lg mb-4">
                Global Coders
              </h3>
              <p className="text-white/80 text-sm leading-relaxed">
                AI-powered English conversation practice for tech professionals.
                Build confidence, improve communication, and ace your next
                conversation.
              </p>
            </div>

            {/* Quick Links */}

            {/* Resources */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-lg mb-4">Resources</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/faq"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    FAQ
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy-policy"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms-of-service"
                    className="text-white/80 hover:text-white text-sm transition-colors duration-200"
                  >
                    Terms of Service
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div className="space-y-4">
              <h3 className="text-white font-bold text-lg mb-4">
                Get in Touch
              </h3>
              <p className="text-white/80 text-sm leading-relaxed">
                Have questions or feedback? We'd love to hear from you. Contact
                us on support@globalcoders.app - we do respond to all emails!
              </p>
            </div>
          </div>

          {/* Funding section */}
          <div className="border-t border-white/10 pt-8 pb-8">
            <div className="flex justify-center items-center mb-8">
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-3 shadow-2xl">
                <img
                  src={footerFunding}
                  alt="Financiado por la Unión Europea - NextGenerationEU. Con el apoyo de la Generalitat de Catalunya"
                  className="max-w-full h-auto"
                  style={{
                    maxHeight: "140px",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-white/10 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-white/60 text-sm">
                © {new Date().getFullYear()} Global Coders. All rights
                reserved.
              </p>
              <div className="flex items-center gap-2 text-white/60 text-sm">
                <span>Made with</span>
                <span className="text-white/80">❤️</span>
                <span>for developers worldwide</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
